import React, { createContext, useContext, useEffect, useState } from 'react';

import { Outlet, useSearchParams } from 'react-router-dom';

import dispatchAndPrintLabels from 'commons/utils/hooks/dispatchAndPrintLabels';
import { fetch as fetchBalance } from 'commons/utils/query/balance';
import { fetchStatementSimulate } from 'commons/utils/query/billing';
import { DocumentsResponseDto } from 'commons/utils/types/documents-response-dto';

import { DispatchOnEnoughBalanceResult } from 'domains/checkout/dispatch-on-enough-balance-result';

import usePrintReports from './usePrintReports';

type CheckoutSuccessState = {
  isDispatching: boolean;
  isDone: boolean;
  result: DispatchOnEnoughBalanceResult;
  failedOrdersIds: string[];
};

const CheckoutSuccessContext = createContext<CheckoutSuccessState | undefined>(
  undefined
);

const useCheckoutSuccessContext = (): CheckoutSuccessState => {
  const context = useContext(CheckoutSuccessContext);

  if (!context) {
    throw new Error(
      'useCheckoutSuccessContext must be used within a CheckoutSuccessProvider'
    );
  }

  return context;
};

const fetchMinBalance = async (
  orderIds: string[]
): Promise<number | undefined> => {
  try {
    return (await fetchStatementSimulate(orderIds)).chargeableAmount;
  } catch (_error) {
    return undefined;
  }
};

const hasMinBalance = async (minBalance: number) => {
  const { currentBalance }: { currentBalance: number } = await fetchBalance();
  return currentBalance >= minBalance;
};

const useDispatchOnEnoughBalance = (
  orderIds: string,
  maxAttempts: number
): CheckoutSuccessState => {
  const [isDispatching, setIsDispatching] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [failedOrdersIds, setFailedOrdersIds] = useState<string[]>([]);
  const [result, setResult] = useState<DispatchOnEnoughBalanceResult>(
    DispatchOnEnoughBalanceResult.None
  );
  const { handleDownload } = usePrintReports();

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    const finish = () => {
      clearInterval(intervalId);
      setIsDone(true);
    };

    if (orderIds.length === 0) {
      finish();
      return;
    }

    const attemptDispatch = async (orderIds: string[], minBalance: number) => {
      if (!(await hasMinBalance(minBalance))) return;

      clearInterval(intervalId);
      setIsDispatching(true);

      const response: DocumentsResponseDto = await dispatchAndPrintLabels({
        contentDeclaration: false,
        orderIds: orderIds,
        handleDownload
      });

      const errorCount = response.errors.length;
      const totalOrders = orderIds.length;

      const allDispatchesSucceeded = errorCount === 0;
      const allDispatchesFailed = errorCount === totalOrders;

      if (allDispatchesSucceeded) {
        setResult(DispatchOnEnoughBalanceResult.Success);
      } else if (allDispatchesFailed) {
        setResult(DispatchOnEnoughBalanceResult.TotalDispatchError);
      } else {
        setResult(DispatchOnEnoughBalanceResult.PartialDispatchError);
        const failedIds = getFailedOrdersIds(response);
        setFailedOrdersIds(failedIds);
      }
      finish();
    };

    const startPolling = async (orderIds: string[]) => {
      const minBalance = await fetchMinBalance(orderIds);

      if (!minBalance) {
        finish();
        return;
      }

      let pollingAttempts = 0;

      intervalId = setInterval(async () => {
        try {
          await attemptDispatch(orderIds, minBalance);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
          finish();
          return;
        }

        pollingAttempts++;

        const hasReachedAttemptsLimit = pollingAttempts >= maxAttempts;
        if (hasReachedAttemptsLimit) {
          setResult(DispatchOnEnoughBalanceResult.BalanceTimeout);
          finish();
        }
      }, 5000);
    };

    void startPolling(orderIds.split(','));

    return () => clearInterval(intervalId);
  }, [orderIds, maxAttempts, handleDownload]);

  return { isDispatching, isDone, failedOrdersIds, result };
};

function getFailedOrdersIds(response: DocumentsResponseDto) {
  return response.errors
    .map((error) => error.deliveryOrderId)
    .filter((orderId): orderId is string => orderId !== undefined);
}

const MAX_POLLING_ATTEMPTS = 12;

const CheckoutSuccessProvider: React.FC = () => {
  const [searchParams] = useSearchParams();

  const state = useDispatchOnEnoughBalance(
    searchParams.get('orderIds') ?? '',
    MAX_POLLING_ATTEMPTS
  );
  return (
    <CheckoutSuccessContext.Provider value={state}>
      <Outlet />
    </CheckoutSuccessContext.Provider>
  );
};

export { useCheckoutSuccessContext, CheckoutSuccessProvider };
